import { useEffect, useState } from 'react'
import Link from 'next/link'

interface NavigationData {
	navigation: {
		menu_items: Array<{
			menu_items_id: {
				label: string
				href: string
				submenu?: {
					label: string
					href: string
					submenu?: {
						label: string
						href: string
					}[]
				}[]
			}
		}>
	}
}

interface NavigationMenuProps {
	isOpen: boolean
	onClose: () => void
	data: NavigationData
}

interface MenuItem {
	title: string
	href: string
	subItems?: {
		title: string
		href: string
		subSubItems?: { title: string; href: string }[]
	}[]
}

const ChevronIcon = ({ isExpanded }: { isExpanded: boolean }) => (
	<svg
		width='24'
		height='24'
		viewBox='0 0 24 24'
		fill='none'
		stroke='currentColor'
		strokeWidth='2'
		strokeLinecap='round'
		strokeLinejoin='round'
		className={`transform transition-transform duration-300 ${
			isExpanded ? 'rotate-180' : ''
		}`}
	>
		<polyline points='6 9 12 15 18 9' />
	</svg>
)

export default function NavigationMenu({
	isOpen,
	onClose,
	data,
}: NavigationMenuProps) {
	const [expandedSections, setExpandedSections] = useState<string[]>([])
	const [expandedSubSections, setExpandedSubSections] = useState<string[]>([])

	useEffect(() => {
		if (isOpen) {
			document.body.style.overflow = 'hidden'
		} else {
			document.body.style.overflow = 'unset'
			setExpandedSections([])
			setExpandedSubSections([])
		}
		return () => {
			document.body.style.overflow = 'unset'
		}
	}, [isOpen])

	const toggleSection = (title: string) => {
		setExpandedSections(prev =>
			prev.includes(title)
				? prev.filter(item => item !== title)
				: [...prev, title]
		)
	}

	const toggleSubSection = (title: string) => {
		setExpandedSubSections(prev =>
			prev.includes(title)
				? prev.filter(item => item !== title)
				: [...prev, title]
		)
	}

	const menuItems: MenuItem[] =
		data?.navigation?.menu_items?.map(item => ({
			title: item.menu_items_id.label,
			href: item.menu_items_id.href,
			...(item.menu_items_id.submenu &&
				item.menu_items_id.submenu.length > 0 && {
					subItems: item.menu_items_id.submenu.map(subItem => ({
						title: subItem.label,
						href: subItem.href,
						...(subItem.submenu &&
							subItem.submenu.length > 0 && {
								subSubItems: subItem.submenu.map(subSubItem => ({
									title: subSubItem.label,
									href: subSubItem.href,
								})),
							}),
					})),
				}),
		})) || []

	return (
		<>
			{/* Overlay */}
			<div
				className={`fixed inset-0 bg-black/50 transition-opacity duration-300 z-40
          ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
				onClick={onClose}
			/>

			{/* Menu Panel */}
			<div
				className={`fixed top-[72px] md:top-[90px] bottom-0 right-0 md:max-w-[500px] w-full md:w-[70%] bg-neutral-50 text-black z-[999] 
          transform transition-transform duration-300 ease-in-out overflow-y-auto
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
			>
				<div className='container mx-auto px-8'>
					{/* Close Button */}
					{/* <button
						onClick={onClose}
						className='absolute top-8 left-8 text-black hover:text-black/80 transition-colors'
						aria-label='Close menu'
					>
						<svg
							width='24'
							height='24'
							viewBox='0 0 24 24'
							fill='none'
							stroke='currentColor'
							strokeWidth='2'
						>
							<path d='M18 6L6 18M6 6l12 12' />
						</svg>
					</button> */}

					
					{/* Main Menu */}
					<nav className='pt-8 pb-8'>
						<ul className='space-y-4 p-0'>
							{menuItems.map(item => (
								<li
									key={item.title}
									className='border-b border-black/20 pb-0 list-none'
								>
									<div className='flex items-center justify-between pb-4'>
										{item.subItems && item.subItems.length > 0 ? (
											<span
												className='text-xl tracking-wider font-agopus hover:text-black/80 transition-colors cursor-pointer'
												onClick={() => toggleSection(item.title)}
											>
												{item.title}
											</span>
										) : (
											<Link
												href={item.href}
												className='text-xl tracking-wider font-agopus hover:text-black/80 transition-colors'
												onClick={onClose}
											>
												{item.title}
											</Link>
										)}
										{item.subItems && item.subItems.length > 0 && (
											<button
												className='text-black hover:text-black/80 transition-colors cursor-pointer'
												onClick={() => toggleSection(item.title)}
												aria-label={`${expandedSections.includes(item.title) ? 'Collapse' : 'Expand'} ${item.title} submenu`}
												aria-expanded={expandedSections.includes(item.title)}
											>
												<ChevronIcon
													isExpanded={expandedSections.includes(item.title)}
												/>
											</button>
										)}
									</div>
									{item.subItems &&
										item.subItems.length > 0 &&
										expandedSections.includes(item.title) && (
											<ul className='pl-4 pb-4 space-y-3 list-none'>
												{item.subItems.map(subItem => (
													<li key={subItem.title} className='pb-0 list-none'>
														<div className='flex items-center justify-between'>
															{subItem.subSubItems &&
															subItem.subSubItems.length > 0 ? (
																<span
																	className='text-lg text-black/80 hover:text-black transition-colors cursor-pointer'
																	onClick={() =>
																		toggleSubSection(subItem.title)
																	}
																>
																	{subItem.title}
																</span>
															) : (
																<Link
																	href={subItem.href}
																	className='text-lg text-black/80 hover:text-black transition-colors'
																	onClick={onClose}
																>
																	{subItem.title}
																</Link>
															)}
															{subItem.subSubItems &&
																subItem.subSubItems.length > 0 && (
																	<button
																		className='text-black hover:text-black/80 transition-colors cursor-pointer'
																		onClick={() =>
																			toggleSubSection(subItem.title)
																		}
																		aria-label={`${expandedSubSections.includes(subItem.title) ? 'Collapse' : 'Expand'} ${subItem.title} submenu`}
																		aria-expanded={expandedSubSections.includes(subItem.title)}
																	>
																		<ChevronIcon
																			isExpanded={expandedSubSections.includes(
																				subItem.title
																			)}
																		/>
																	</button>
																)}
														</div>
														{subItem.subSubItems &&
															subItem.subSubItems.length > 0 &&
															expandedSubSections.includes(subItem.title) && (
																<ul className='pl-4 pt-3 space-y-3 list-none'>
																	{subItem.subSubItems.map(subSubItem => (
																		<li key={subSubItem.title} className='pb-0'>
																			<Link
																				href={subSubItem.href}
																				className='text-base text-black/60 hover:text-black transition-colors'
																				onClick={onClose}
																			>
																				{subSubItem.title}
																			</Link>
																		</li>
																	))}
																</ul>
															)}
													</li>
												))}
											</ul>
										)}
								</li>
							))}
						</ul>
					</nav>
				</div>
			</div>
		</>
	)
}
